import Phaser from 'phaser';

/** Акцентный цвет игры (как primary/Continue): светлый, не жёлтый */
const ARROW_COLOR = 0xe8e8e8;
/** Затемнение доски при перетаскивании в step1 */
const BOARD_DIM_ALPHA = 0.35;
/** Подсветка слота «сюда»: обводка и мягкое свечение */
const SLOT_STROKE_ALPHA = 0.85;
const SLOT_PADDING = 8;
const SLOT_STROKE_WIDTH = 2.5;
const SLOT_GLOW_WIDTH = 12;
const SLOT_GLOW_ALPHA = 0.2;
const BOARD_SPOTLIGHT_DEPTH = 49998.5;
const BOARD_CORNER_RADIUS = 14;
const SLOT_CORNER_RADIUS = 8;
const ARROW_ALPHA = 0.85;
const ARROW_LINE_WIDTH = 3.5;
const ARROW_GLOW_WIDTH = 8;
const ARROW_GLOW_ALPHA = 0.22;
const HEAD_SIZE = 11;
const ARC_OFFSET = 42;
/** Старт дуги стрелки ниже bubble (виртуальный anchor), зазор 10–14px */
const GAP_BELOW_BUBBLE = 12;
/** Контрольная точка дуги не выше этой Y (локальные координаты), чтобы дуга не касалась bubble */
const CP_MIN_Y_BELOW_BUBBLE = 10;
const BOUNCE_AMPLITUDE = 15;
const BOUNCE_SCALE_MIN = 0.98;
const BOUNCE_SCALE_MAX = 1.03;
const DIM_ALPHA = 0.2;
const HINT_MAX_WIDTH_RATIO = 0.7;
const FLOW_ALPHA_MIN = 0.6;
const FLOW_ALPHA_MAX = 1;
const FLOW_DURATION = 1600;

/**
 * Создаёт контейнер для изогнутой стрелки (дуга + наконечник).
 * Внутри: graphics для рисования.
 */
export function createTutorialArrow(scene: Phaser.Scene): Phaser.GameObjects.Container {
  const outer = scene.add.container(0, 0).setScrollFactor(0).setDepth(2050);
  // Не интерактивен; setInteractive без hitArea даёт warning в Phaser.
  outer.disableInteractive();
  const g = scene.add.graphics();
  outer.add(g);
  return outer;
}

export type ViewportPoint = { x: number; y: number };

/**
 * Рисует изогнутую стрелку: старт дуги на 12px ниже bubble (air gap), конец у цели.
 * Контрольная точка ниже bubble, чтобы дуга не касалась плашки.
 */
export function updateTutorialArrow(
  arrow: Phaser.GameObjects.Container,
  from: ViewportPoint,
  to: ViewportPoint
): void {
  const g = arrow.list[0] as Phaser.GameObjects.Graphics | undefined;
  if (!g) return;

  arrow.setPosition(from.x, from.y);

  const startX = 0;
  const startY = GAP_BELOW_BUBBLE;
  const endX = to.x - from.x;
  const endY = to.y - from.y;
  const segDx = endX - startX;
  const segDy = endY - startY;
  const segLen = Math.sqrt(segDx * segDx + segDy * segDy);
  if (segLen < 4) {
    g.clear();
    return;
  }

  const midX = (startX + endX) * 0.5;
  const midY = (startY + endY) * 0.5;
  const perpX = -segDy / segLen;
  const perpY = segDx / segLen;
  let cpX = midX + perpX * ARC_OFFSET;
  let cpY = midY + perpY * ARC_OFFSET;
  cpY = Math.max(CP_MIN_Y_BELOW_BUBBLE, cpY);

  const tangentX = endX - cpX;
  const tangentY = endY - cpY;
  const tLen = Math.sqrt(tangentX * tangentX + tangentY * tangentY) || 1;
  const tx = tangentX / tLen;
  const ty = tangentY / tLen;
  const baseX = endX - tx * HEAD_SIZE;
  const baseY = endY - ty * HEAD_SIZE;
  const headPerpX = -ty;
  const headPerpY = tx;
  const halfW = HEAD_SIZE * 0.45;
  const leftX = baseX + headPerpX * halfW;
  const leftY = baseY + headPerpY * halfW;
  const rightX = baseX - headPerpX * halfW;
  const rightY = baseY - headPerpY * halfW;

  g.clear();

  const curve = new Phaser.Curves.QuadraticBezier(
    new Phaser.Math.Vector2(startX, startY),
    new Phaser.Math.Vector2(cpX, cpY),
    new Phaser.Math.Vector2(endX, endY)
  );

  g.lineStyle(ARROW_GLOW_WIDTH, ARROW_COLOR, ARROW_GLOW_ALPHA);
  curve.draw(g, 32);

  g.lineStyle(ARROW_LINE_WIDTH, ARROW_COLOR, ARROW_ALPHA);
  curve.draw(g, 32);

  g.fillStyle(ARROW_COLOR, ARROW_ALPHA);
  g.beginPath();
  g.moveTo(endX, endY);
  g.lineTo(leftX, leftY);
  g.lineTo(rightX, rightY);
  g.closePath();
  g.fillPath();
}

/**
 * Нижний центр элемента подсказки в координатах viewport камеры.
 */
export function getHintBottomCenterInViewport(
  hintEl: HTMLElement,
  canvasRect: DOMRect,
  cam: Phaser.Cameras.Scene2D.Camera
): ViewportPoint {
  const r = hintEl.getBoundingClientRect();
  const scaleX = canvasRect.width > 0 ? cam.width / canvasRect.width : 1;
  const scaleY = canvasRect.height > 0 ? cam.height / canvasRect.height : 1;
  return {
    x: (r.left + r.width / 2 - canvasRect.left) * scaleX,
    y: (r.bottom - canvasRect.top) * scaleY,
  };
}

/**
 * Запускает лёгкий flow-эффект: пульсация alpha (0.6 → 1.0), без bounce.
 */
export function startArrowFlow(
  scene: Phaser.Scene,
  arrow: Phaser.GameObjects.Container
): () => void {
  const g = arrow.list[0] as Phaser.GameObjects.Graphics;
  if (!g) return () => {};
  g.setAlpha(FLOW_ALPHA_MIN);
  const tween = scene.tweens.add({
    targets: g,
    alpha: FLOW_ALPHA_MAX,
    duration: FLOW_DURATION / 2,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  });
  return () => {
    tween.stop();
    g.setAlpha(FLOW_ALPHA_MAX);
  };
}

/**
 * Параметры анимации подпрыгивания целевого кусочка.
 */
export const STEP1_BOUNCE = {
  amplitudeY: BOUNCE_AMPLITUDE,
  scaleMin: BOUNCE_SCALE_MIN,
  scaleMax: BOUNCE_SCALE_MAX,
};

export const STEP1_DIM_ALPHA = DIM_ALPHA;

export function startTargetBounce(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Image,
  onStop?: () => void
): () => void {
  const startY = target.y;
  const startScaleX = target.scaleX;
  const startScaleY = target.scaleY;

  const tweenY = scene.tweens.add({
    targets: target,
    y: startY + BOUNCE_AMPLITUDE,
    duration: 500,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  });
  const tweenScale = scene.tweens.add({
    targets: target,
    scaleX: BOUNCE_SCALE_MAX * startScaleX,
    scaleY: BOUNCE_SCALE_MAX * startScaleY,
    duration: 400,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  });

  const stop = (): void => {
    tweenY.stop();
    tweenScale.stop();
    target.y = startY;
    target.scaleX = startScaleX;
    target.scaleY = startScaleY;
    onStop?.();
  };
  return stop;
}

/**
 * Создаёт лёгкую «всплывающую» подсказку (не как кнопка).
 */
export function createTutorialHintDom(text: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'tutorial-hint tutorial-hint-floating';
  el.setAttribute('aria-live', 'polite');
  el.style.cssText = [
    'position:absolute',
    'left:50%',
    'transform:translateX(-50%)',
    'padding:8px 12px',
    'background:rgba(0,0,0,0.4)',
    'color:rgba(255,255,255,0.9)',
    'border-radius:10px',
    'font-size:13px',
    'line-height:1.2',
    'max-width:70%',
    'text-align:center',
    'box-shadow:0 2px 8px rgba(0,0,0,0.2)',
    'z-index:1100',
    'pointer-events:none',
    'opacity:0',
    'transition:opacity 180ms ease-out',
  ].join(';');
  el.textContent = text;
  const root = document.getElementById('ui-root') ?? document.body;
  root.appendChild(el);

  requestAnimationFrame(() => {
    el.style.opacity = '1';
  });

  el.classList.add('tutorial-hint-float');
  return el;
}

/**
 * Позиционирует подсказку над лотком.
 */
export function positionTutorialHint(
  hintEl: HTMLElement,
  trayRectViewport: { x: number; y: number; w: number; h: number },
  canvasRect: DOMRect,
  viewportHeight: number
): void {
  const scaleY = canvasRect.height > 0 ? viewportHeight / canvasRect.height : 1;
  const topViewport = trayRectViewport.y - 8;
  const topPx = canvasRect.top + topViewport / scaleY;
  hintEl.style.bottom = '';
  hintEl.style.top = `${topPx - 52}px`;
  hintEl.style.left = '50%';
  hintEl.style.transform = 'translateX(-50%)';
  const maxW = Math.min(window.innerWidth * HINT_MAX_WIDTH_RATIO, 280);
  hintEl.style.maxWidth = `${maxW}px`;
}

export function destroyTutorialHint(el: HTMLElement | null): void {
  if (!el?.parentNode) return;
  el.parentNode.removeChild(el);
}

// --- Welcome header (above board, Step 1 + Step 2) ---

const WELCOME_LINE1 = 'Добро пожаловать!';
const WELCOME_LINE2 = 'Собери свой первый пазл';
/** Отступ от низа welcome-блока до верха доски. */
const WELCOME_GAP_ABOVE_BOARD_VIEWPORT = 8;
/** Отступ слева от левого края доски (px в экранных координатах). */
const WELCOME_LEFT_OFFSET_PX = 18;
const WELCOME_FADE_MS = 200;

/**
 * Создаёт приветственный туториал-лейбл над доской: иконка-пазлик + две строки текста.
 */
export function createTutorialWelcomeHeader(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'tutorial-welcome-header';
  wrap.setAttribute('aria-label', `${WELCOME_LINE1}. ${WELCOME_LINE2}`);
  wrap.style.cssText = [
    'position:fixed',
    'display:flex',
    'flex-direction:row',
    'align-items:flex-start',
    'gap:8px',
    'max-width:100%',
    'pointer-events:none',
    'z-index:1090',
    'opacity:0',
    `transition:opacity ${WELCOME_FADE_MS}ms ease-out`,
    'letter-spacing:0.02em',
    'line-height:1.15',
  ].join(';');

  const icon = document.createElement('span');
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = '👋';
  icon.style.cssText = [
    'font-size:clamp(12px, 2.8vw, 15px)',
    'line-height:1',
    'flex-shrink:0',
    'opacity:0.9',
    'filter:var(--ui-capsule-icon-filter)',
  ].join(';');

  const textBlock = document.createElement('div');
  textBlock.style.cssText = 'display:flex;flex-direction:column;min-width:0;flex:1';

  const textShadow = '0 1px 3px rgba(0,0,0,0.7), 0 0 12px rgba(0,0,0,0.4)';

  const line1 = document.createElement('div');
  line1.textContent = WELCOME_LINE1;
  line1.style.cssText = [
    'color:rgba(255,255,255,0.84)',
    'font-size:clamp(11px, 3vw, 16px)',
    'font-weight:500',
    'line-height:1.15',
    'margin:0',
    'letter-spacing:0.02em',
    `text-shadow:${textShadow}`,
  ].join(';');

  const line2 = document.createElement('div');
  line2.textContent = WELCOME_LINE2;
  line2.style.cssText = [
    'color:rgba(255,255,255,0.66)',
    'font-size:clamp(10px, 2.6vw, 13px)',
    'font-weight:400',
    'line-height:1.2',
    'margin:3px 0 0',
    'letter-spacing:0.02em',
    `text-shadow:${textShadow}`,
  ].join(';');

  textBlock.appendChild(line1);
  textBlock.appendChild(line2);
  wrap.appendChild(icon);
  wrap.appendChild(textBlock);

  const root = document.getElementById('ui-root') ?? document.body;
  root.appendChild(wrap);

  requestAnimationFrame(() => {
    wrap.style.opacity = '1';
  });

  return wrap;
}

/**
 * Позиционирует welcome-блок: по левому краю доски (x = left(board) + offset), под topBar с отступом, ширина ≤ boardRect.
 */
export function positionTutorialWelcomeHeader(
  el: HTMLElement,
  boardRectViewport: { x: number; y: number; w: number; h: number },
  canvasRect: DOMRect,
  viewportWidth: number,
  viewportHeight: number
): void {
  if (canvasRect.width <= 0 || canvasRect.height <= 0) return;
  const scaleX = viewportWidth / canvasRect.width;
  const scaleY = viewportHeight / canvasRect.height;
  const bottomViewport = boardRectViewport.y - WELCOME_GAP_ABOVE_BOARD_VIEWPORT;
  const bottomPx = canvasRect.top + bottomViewport / scaleY;
  const bottomCss = window.innerHeight - bottomPx;
  const leftPx = canvasRect.left + boardRectViewport.x / scaleX + WELCOME_LEFT_OFFSET_PX;
  const maxWidthPx = boardRectViewport.w / scaleX - WELCOME_LEFT_OFFSET_PX;

  el.style.top = '';
  el.style.bottom = `${bottomCss}px`;
  el.style.left = `${leftPx}px`;
  el.style.transform = '';
  el.style.maxWidth = `${Math.max(120, maxWidthPx)}px`;
}

export function destroyTutorialWelcomeHeader(el: HTMLElement | null): void {
  if (!el?.parentNode) return;
  el.parentNode.removeChild(el);
}

export type BoardRect = { x: number; y: number; w: number; h: number };

/**
 * Создаёт Graphics для затемнения доски и подсветки целевого слота (step1).
 * Не интерактивен. Глубина: выше доски, ниже перетаскиваемого кусочка.
 */
export function createBoardSpotlight(scene: Phaser.Scene): Phaser.GameObjects.Graphics {
  const g = scene.add
    .graphics()
    .setScrollFactor(0)
    .setDepth(BOARD_SPOTLIGHT_DEPTH)
    .setVisible(false);
  g.setInteractive(undefined, () => false);
  g.disableInteractive();
  return g;
}

/**
 * Рисует затемнение доски (boardRect) и обводку целевого слота (targetRect).
 * targetRect — с отступом SLOT_PADDING для «мягкой» подсветки.
 */
export function updateBoardSpotlight(
  g: Phaser.GameObjects.Graphics,
  boardRect: BoardRect | null,
  targetRect: BoardRect | null
): void {
  g.clear();
  if (boardRect) {
    g.fillStyle(0x000000, BOARD_DIM_ALPHA);
    g.fillRoundedRect(boardRect.x, boardRect.y, boardRect.w, boardRect.h, BOARD_CORNER_RADIUS);
  }
  if (targetRect) {
    const px = targetRect.x - SLOT_PADDING;
    const py = targetRect.y - SLOT_PADDING;
    const pw = targetRect.w + SLOT_PADDING * 2;
    const ph = targetRect.h + SLOT_PADDING * 2;
    g.lineStyle(SLOT_GLOW_WIDTH, ARROW_COLOR, SLOT_GLOW_ALPHA);
    g.strokeRoundedRect(px, py, pw, ph, SLOT_CORNER_RADIUS + SLOT_PADDING * 0.5);
    g.lineStyle(SLOT_STROKE_WIDTH, ARROW_COLOR, SLOT_STROKE_ALPHA);
    g.strokeRoundedRect(px, py, pw, ph, SLOT_CORNER_RADIUS + SLOT_PADDING * 0.5);
  }
}
